import { ReactNode, useEffect, useMemo } from 'react';
import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  useIsMobile,
  useToggle,
  CrossIcon,
  MarketIcon,
  Card,
  WalletIcon,
  MenuIcon,
  CollapsableSidebar,
  ActivityIcon,
  CryptoIcon,
  PiggyBankIcon,
  InfoCircleIcon,
  LiquidationIcon,
  DangerIcon
} from '@blockanalitica/ui';
import { Link, Outlet, useLocation, useParams } from 'react-router';
import styled, { css } from 'styled-components';
import Footer from './Footer';
import Navigation, { NavigationRoute } from './Navigation';
import { NETWORKS_MAP } from '@/constants';
import { titleCase } from '@/utils';
import { LinkButton, ExternalLinkIcon } from '@/components';
import {
  default as SparkLogo
  // @ts-ignore
} from '@/assets/logos/spark_logo.svg?react';
// import UnavailableIcon from '../icons/UnavailableIcon';

const Header = styled(Flex)<FlexProps>`
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-weight: 500;
  flex: 1;
  box-sizing: border-box;
  height: 100%;

  ${(props) => css`
    border: solid 1px ${props.theme.colors.spark.gray90};
    background-color: ${props.theme.colors.spark.gray90};
    border-radius: ${props.theme.sizes['-3']};
    gap: ${props.theme.space['xs']};
    font-size: ${props.theme.sizes['-1']};
    padding: ${props.theme.space['2xs']};

    &:hover {
      background-color: ${props.theme.colors.spark.gray80};
    }
  `}
`;

const ContentContainer = styled(Flex)<FlexProps>`
  box-sizing: border-box;
  padding: ${(props) =>
    `${props.theme.space['s']} ${props.theme.space['s-m']} ${props.theme.space['s-m']}`};
`;

const SidebarButton = styled(Flex)<FlexProps>`
  cursor: pointer;
  &:hover {
    color: ${(props) => props.theme.colors.linkHover};
  }
`;

const CloseButton = styled(Box)<BoxProps>`
  cursor: pointer;
  &:hover {
    color: ${(props) => props.theme.colors.linkHover};
  }
`;

const AlphaBackgroundLayer = styled.div`
  display: block;
  position: fixed;
  inset: 0;
  background-color: ${(props) => props.theme.colors.black65};
  z-index: 49;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
`;

function createNavigationRoutes(network: string): NavigationRoute[] {
  const routes = [
    // {
    //   title: 'Home',
    //   linkTo: `/`,
    //   icon: <HomeIcon size="1" />
    // },
    {
      title: titleCase(network),
      linkTo: '',
      icon: <CryptoIcon name={NETWORKS_MAP[network].icon} size="1" />,
      end: true
    },
    {
      title: 'Markets',
      linkTo: '/markets',
      icon: <MarketIcon size="1" />
    },
    {
      title: 'Wallets',
      linkTo: '/wallets',
      icon: <WalletIcon size="1" />
    },
    // {
    //   title: 'Bad debt',
    //   linkTo: '/bad-debt',
    //   icon: <UnavailableIcon size="1" />
    // },
    {
      title: 'Liquidations',
      linkTo: '/liquidations',
      icon: <LiquidationIcon size="1" />
    },
    {
      title: 'Parameters',
      linkTo: '/parameters',
      icon: <InfoCircleIcon size="1" />
    },
    {
      title: 'Activity',
      linkTo: '/activity',
      icon: <ActivityIcon size="1" />
    },
    {
      title: 'Treasury',
      linkTo: '/treasury',
      icon: <PiggyBankIcon size="1" />
    },
    {
      title: 'Risk',
      linkTo: '/risk',
      icon: <DangerIcon size="1" />
    }
  ];
  return routes;
}

export default function Layout({
  children
}: {
  children?: ReactNode | ReactNode[];
}) {
  const [sidebarOpen, toggleSidebar] = useToggle();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { network } = useParams() as { network: string };

  const navigationRoutes = useMemo(
    () => createNavigationRoutes(network),
    [network]
  );

  useEffect(() => {
    toggleSidebar(false);
  }, [location.pathname, toggleSidebar]);

  return (
    <>
      <CollapsableSidebar
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        alignItems="flex-start">
        <Flex justifyContent="space-between" width="100%">
          <SparkLogo height={isMobile ? 16 : 24} alt="SparkLend" />

          <CloseButton alignSelf="center" onClick={() => toggleSidebar(false)}>
            <CrossIcon size="0" />
          </CloseButton>
        </Flex>

        <Navigation
          routes={navigationRoutes}
          flexDirection="column"
          alignItems="flex-start"
          gap="s"
        />
      </CollapsableSidebar>

      <ContentContainer
        flexGrow={1}
        flexShrink={1}
        flexBasis={['100%', 'auto']}
        margin={'0 auto'}
        width="100%"
        maxWidth={['100vw', '80rem']}
        flexDirection="column"
        justifyContent="space-between"
        gap="m">
        <Flex justifyContent="space-between" alignItems="center">
          <Link to="/">
            <SparkLogo height={isMobile ? 24 : 28} alt="SparkLend" />
          </Link>
          <Card
            flex="0"
            flexDirection="row"
            padding="2xs"
            gap="s"
            alignItems="center">
            <LinkButton
              as="a"
              // @ts-ignore
              href="https://app.spark.fi"
              target="_blank"
              rel="noreferrer"
              variant="sparkApp"
              gap="2xs">
              Spark App
              <ExternalLinkIcon size="1" />
            </LinkButton>
            <SidebarButton>
              <Header onClick={() => toggleSidebar()}>
                <MenuIcon size="2" />
              </Header>
            </SidebarButton>
          </Card>
        </Flex>

        <main>{children ? children : <Outlet />}</main>

        <Footer />
      </ContentContainer>

      {sidebarOpen ? (
        <AlphaBackgroundLayer onClick={() => toggleSidebar()} />
      ) : null}
    </>
  );
}
